import {useState, useMemo} from "react";
import moment from "moment";
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  Select,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Grid,
  Chip,
  Button,
} from "@mui/material";
import Page from "src/components/Page";
import Scrollbar from "src/components/Scrollbar";
import SearchNotFound from "src/components/SearchNotFound";
import ListHead from "src/components/common/ListHead";
import MyPagination from "src/components/common/MyPagination";
import {asyncRequest} from "src/services/network";
import {alert} from "src/services/alert";

const TABLE_HEAD = [
  {id: "id", label: "ID", alignRight: false, width: 80},
  {id: "admin", label: "操作人", alignRight: false, width: 120},
  {id: "operation", label: "操作类型", alignRight: false, width: 130},
  {id: "target_user", label: "目标用户", alignRight: false, width: 150},
  {id: "detail", label: "操作详情", alignRight: false},
  {id: "remark", label: "备注", alignRight: false, width: 150},
  {id: "create_time", label: "时间", alignRight: false, width: 170},
];

const OPERATION_OPTIONS = [
  {value: "", label: "全部"},
  {value: "adjust_assets", label: "资产调整"},
  {value: "update_inviter", label: "修改直推"},
  {value: "unfreeze_direct_value", label: "冻结转可用"},
  {value: "unfreeze_equity_value", label: "权益值冻结转可用"},
  {value: "update_release_setting", label: "释放设置"},
  {value: "batch_coin_recharge", label: "批量拨付金豆"},
];

const OPERATION_LABEL_MAP = {
  adjust_assets: "资产调整",
  update_inviter: "修改直推",
  unfreeze_direct_value: "冻结转可用",
  unfreeze_equity_value: "权益值冻结转可用",
  update_release_setting: "释放设置",
  batch_coin_recharge: "批量拨付金豆",
};

const OPERATION_COLOR_MAP = {
  adjust_assets: "warning",
  update_inviter: "info",
  unfreeze_direct_value: "success",
  unfreeze_equity_value: "success",
  update_release_setting: "secondary",
  batch_coin_recharge: "error",
};

export default function JytAdminOperationLogList() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [hasQueried, setHasQueried] = useState(false);

  const defaultTimeRange = useMemo(() => {
    const now = new Date();
    const end = moment(now).format("YYYY-MM-DDTHH:mm");
    const start = moment(now).subtract(7, "days").format("YYYY-MM-DDTHH:mm");
    return {startDate: start, endDate: end};
  }, []);

  const [filter, setFilter] = useState({
    page: 0,
    size: 20,
    operation: "",
    adminName: "",
    targetUserId: "",
    startDate: defaultTimeRange.startDate,
    endDate: defaultTimeRange.endDate,
  });

  async function getItems(filterOverride) {
    setItems(null);
    const queryFilter = filterOverride || filter;
    const {data, count} = await asyncRequest({
      path: "/jingxi/jyt-admin-operation-log/list",
      data: queryFilter,
    });
    setItems(data || []);
    setTotal(count);
    setHasQueried(true);
  }

  const handlePageChange = (event, value, newSize) => {
    let newFilter;
    if (newSize !== undefined) {
      newFilter = {...filter, page: 0, size: newSize};
    } else {
      const newPage = value - 1;
      if (newPage === filter.page) return;
      newFilter = {...filter, page: newPage};
    }
    setFilter(newFilter);
    if (hasQueried) getItems(newFilter);
  };

  function handleReset() {
    setHasQueried(false);
    setItems(null);
    setTotal(0);
    setFilter({
      page: 0,
      size: 20,
      operation: "",
      adminName: "",
      targetUserId: "",
      startDate: defaultTimeRange.startDate,
      endDate: defaultTimeRange.endDate,
    });
  }

  function formatDetail(detail, operation) {
    if (!detail) return "-";
    try {
      const obj = typeof detail === "string" ? JSON.parse(detail) : detail;
      if (operation === "adjust_assets") {
        const fieldMap = {score: "银豆", coinRecharge: "充值赠送金豆", equityValue: "现存权益值", frozenDirectValue: "冻结直推业绩", frozenEquityValue: "冻结权益值"};
        const opMap = {add: "增加", subtract: "减少"};
        return `${fieldMap[obj.field] || obj.field} ${opMap[obj.operation] || obj.operation} ${obj.amount}（${obj.before} → ${obj.after}）`;
      }
      if (operation === "update_inviter") {
        return `上级ID（原系统ID）：${obj.old_inviter_plat_id ?? obj.old_inviter_id ?? "无"} → ${obj.new_inviter_plat_id ?? obj.new_inviter_id ?? "无"}`;
      }
      if (operation === "unfreeze_direct_value") {
        return `转换 ${obj.amount}，冻结前：${obj.frozen_before}`;
      }
      if (operation === "unfreeze_equity_value") {
        return `转换 ${obj.amount}，冻结前：${obj.frozen_before}`;
      }
      if (operation === "update_release_setting") {
        const parts = [];
        if (obj.auto_release) parts.push(`自动释放：${obj.auto_release.from} → ${obj.auto_release.to}`);
        if (obj.release_rate) parts.push(`释放速率：${obj.release_rate.from} → ${obj.release_rate.to}`);
        return parts.join("；") || "无变更";
      }
      if (operation === "batch_coin_recharge") {
        const parts = [`共${obj.total}条，成功${obj.success}条，失败${obj.fail}条`];
        if (obj.oldUserDeadline) parts.push(`老用户截止：${obj.oldUserDeadline}`);
        if (obj.oldUserAmount) parts.push(`老用户金豆：${obj.oldUserAmount}`);
        return parts.join("；");
      }
      return JSON.stringify(obj);
    } catch {
      return String(detail);
    }
  }

  return (
    <Page title="金银通操作日志">
      <Container maxWidth="xl">
        <Card>
          {/* 搜索栏 */}
          <Stack spacing={2} sx={{p: 2}}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <FormControl size="small" style={{minWidth: 140}}>
                  <InputLabel>操作类型</InputLabel>
                  <Select
                    label="操作类型"
                    value={filter.operation}
                    onChange={(e) => setFilter({...filter, operation: e.target.value, page: 0})}
                  >
                    {OPERATION_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item>
                <TextField
                  size="small"
                  label="操作人"
                  value={filter.adminName}
                  onChange={(e) => setFilter({...filter, adminName: e.target.value, page: 0})}
                  placeholder="管理员名称"
                  autoComplete="off"
                />
              </Grid>
              <Grid item>
                <TextField
                  size="small"
                  label="目标用户ID"
                  type="number"
                  value={filter.targetUserId}
                  onChange={(e) => setFilter({...filter, targetUserId: e.target.value, page: 0})}
                  placeholder="JytCmfUser ID"
                  autoComplete="off"
                />
              </Grid>
              <Grid item>
                <TextField
                  size="small"
                  type="datetime-local"
                  label="开始时间"
                  value={filter.startDate}
                  onChange={(e) => setFilter({...filter, startDate: e.target.value, page: 0})}
                  InputLabelProps={{shrink: true}}
                />
              </Grid>
              <Grid item>
                <TextField
                  size="small"
                  type="datetime-local"
                  label="结束时间"
                  value={filter.endDate}
                  onChange={(e) => setFilter({...filter, endDate: e.target.value, page: 0})}
                  InputLabelProps={{shrink: true}}
                />
              </Grid>
              <Grid item>
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={() => getItems()}>查询</Button>
                  <Button variant="outlined" onClick={handleReset}>重置</Button>
                </Stack>
              </Grid>
            </Grid>
          </Stack>

          <Scrollbar>
            <TableContainer sx={{minWidth: 900}}>
              <Table>
                <ListHead headLabel={TABLE_HEAD}/>
                <TableBody>
                  {items === null ? (
                    <TableRow>
                      <TableCell align="center" colSpan={7}>
                        <Typography variant="body2" color="text.secondary">请点击查询</Typography>
                      </TableCell>
                    </TableRow>
                  ) : items.length === 0 ? (
                    <TableRow>
                      <TableCell align="center" colSpan={7}>
                        <SearchNotFound/>
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((row) => (
                      <TableRow hover key={row.id}>
                        <TableCell align="left">{row.id}</TableCell>
                        <TableCell align="left">
                          <Typography variant="body2">{row.admin_name || "-"}</Typography>
                          <Typography variant="caption" color="text.secondary">ID: {row.admin_id}</Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Chip
                            size="small"
                            label={OPERATION_LABEL_MAP[row.operation] || row.operation}
                            color={OPERATION_COLOR_MAP[row.operation] || "default"}
                          />
                        </TableCell>
                        <TableCell align="left">
                          <Typography variant="body2">{row.target_user_name || "-"}</Typography>
                          <Typography variant="caption" color="text.secondary">ID: {row.target_user_id}</Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography variant="body2" sx={{maxWidth: 350, wordBreak: "break-all"}}>
                            {formatDetail(row.detail, row.operation)}
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography variant="body2">{row.remark || "-"}</Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography variant="body2">{row.create_time ? moment(row.create_time).format("YYYY-MM-DD HH:mm:ss") : "-"}</Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          {total > 0 && (
            <MyPagination
              page={filter.page + 1}
              count={total}
              rowsPerPage={filter.size}
              onPageChange={handlePageChange}
            />
          )}
        </Card>
      </Container>
    </Page>
  );
}
